import React, { useEffect, useRef, useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import prismLogo from '../assets/prism-logo.svg';
import { MonthlyClosingsChart } from './MonthlyClosingsChart';
import { MillionDollarMarketChart } from './MillionDollarMarketChart';
import { SalesPricesChart } from './SalesPricesChart';
import { SalesPSFChart } from './SalesPSFChart';
import { SalesPriceDistributionChart } from './SalesPriceDistributionChart';
import { SalesDistributionChart } from './SalesDistributionChart';
import { LuxVsTraditionalChart } from './LuxVsTraditionalChart';
import { PriceIndexChart } from './PriceIndexChart';
import { ShadowInventoryChart } from './ShadowInventoryChart';
import Highcharts from 'highcharts';
import { useApi } from '../hooks/useApi';
import { useAnalyticsPackage, PackageItem } from '../context/AnalyticsPackageContext';

interface SavedLensDto {
  id: number;
  name: string;
  isSystem: boolean;
  entityScope: string | null;
  activeLensId: number | null;
  activeLensName: string | null;
  items: any[];
}

// ── label lookups ────────────────────────────────────────────────────────────

const PT_LABELS: Record<string, string> = {
  '1F': 'Single Family', 'C': 'Condominium', 'L': 'Land',
};

const CAT_LABELS: Record<number, string> = {
  [-1]: 'All Luxury', [-2]: 'Luxury Full Service',
  1: 'Luxe Hotel', 2: 'Luxe Classic', 3: 'Luxe Boutique', 4: 'Mid Luxe', 7: 'Traditional',
};

function filterSummary(
  propertyType: string | null,
  buildingCategoryId: number | null,
  yearStart: number | null,
  yearEnd: number | null,
  subareaName: string | null,
): string {
  const parts: string[] = [];
  if (propertyType) parts.push(PT_LABELS[propertyType] ?? propertyType);
  if (buildingCategoryId != null) {
    const lbl = CAT_LABELS[buildingCategoryId];
    if (lbl) parts.push(lbl);
  }
  if (yearStart != null && yearEnd != null) parts.push(`${yearStart}–${yearEnd}`);
  if (subareaName) parts.push(subareaName);
  return parts.join(' · ');
}

// sortOrder is stored as 'appreciation'|'openDate', optionally suffixed with
// ':start-end' when the user added a page other than the first to a Lens.
function parseSortOrder(sortOrder: string | null): { mode: 'appreciation' | 'openDate'; range: [number, number] | null } {
  const [modePart, rangePart] = (sortOrder ?? '').split(':');
  const mode = modePart === 'openDate' ? 'openDate' : 'appreciation';
  if (!rangePart) return { mode, range: null };
  const [start, end] = rangePart.split('-').map(Number);
  return { mode, range: [start, end] };
}

function sortLabel(sortOrder: string | null): string {
  const { mode, range } = parseSortOrder(sortOrder);
  const base = mode === 'openDate' ? 'by Building Open Date' : '';
  const rangeLabel = range ? `Buildings ${range[0]}-${range[1]}` : '';
  return [rangeLabel, base].filter(Boolean).join(', ');
}

// ── PDF helpers ──────────────────────────────────────────────────────────────

const PRISM_LOGO_ASPECT = 1179 / 375.935;

function loadImage(src: string, crossOrigin = false): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function fetchSvgAsDataUrl(svgSrc: string, pixW: number, pixH: number): Promise<string | null> {
  try {
    const res = await fetch(svgSrc);
    let text = await res.text();
    text = text.replace(/<svg\b/, `<svg width="${pixW}" height="${pixH}"`);
    const blob = new Blob([text], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const img = await loadImage(url);
    URL.revokeObjectURL(url);
    const c = document.createElement('canvas');
    c.width = pixW; c.height = pixH;
    c.getContext('2d')!.drawImage(img, 0, 0);
    return c.toDataURL('image/png');
  } catch {
    return null;
  }
}

// ── API URL builder ──────────────────────────────────────────────────────────

function itemApiUrl(item: PackageItem): string | null {
  const { entityType, entityId, chartName, propertyType, buildingCategoryId, yearStart, yearEnd } = item;

  const scope = entityType === 'building' ? `buildingId=${entityId}`
              : entityType === 'market'   ? `marketId=${entityId}`
              : `neighborhoodId=${entityId}`;

  const pt    = propertyType ?? 'C';
  const base  = `${scope}&propertyType=${pt}`;

  const luxParam     = buildingCategoryId === -1 ? '&luxOption=true'
                     : buildingCategoryId === -2 ? '&luxFullService=true' : '';
  const catParam     = buildingCategoryId && buildingCategoryId > 0
    ? `&buildingCategoryId=${buildingCategoryId}` : '';
  const yearParams   = yearStart && yearEnd ? `&yearStart=${yearStart}&yearEnd=${yearEnd}` : '';
  const subareaParam = item.subareaId ? `&subareaId=${item.subareaId}` : '';

  if (entityType === 'building') {
    switch (chartName) {
      case 'monthlyClosings':        return `/api/charts/monthly-closings?${scope}`;
      case 'salesPrices':            return `/api/charts/sales-prices?${scope}&propertyType=C`;
      case 'salesPSF':               return `/api/charts/sales-psf?${scope}&propertyType=C`;
      case 'salesPriceDistribution': return `/api/charts/price-range-trends?${scope}&propertyType=C`;
      default: return null;
    }
  }

  switch (chartName) {
    case 'monthlyClosings':        return `/api/charts/monthly-closings?${base}${subareaParam}`;
    case 'millionDollarMarket':    return `/api/charts/million-dollar-market?${base}${yearParams}${subareaParam}`;
    case 'salesPrices':            return `/api/charts/sales-prices?${base}${yearParams}${luxParam}${catParam}${subareaParam}`;
    case 'salesPSF':               return `/api/charts/sales-psf?${base}${yearParams}${luxParam}${catParam}${subareaParam}`;
    case 'salesPriceDistribution': return `/api/charts/price-range-trends?${base}${yearParams}${luxParam}${catParam}${subareaParam}`;
    case 'salesDistribution':      return `/api/charts/sales-distribution?${base}${yearParams}${subareaParam}`;
    case 'luxVsTraditional':       return `/api/charts/lux-vs-traditional?${scope}${yearParams}${subareaParam}`;
    case 'priceIndex':             return `/api/charts/price-index?${base}${yearParams}`;
    case 'buildingAppreciation':   return `/api/charts/building-appreciation?${scope}${luxParam}${catParam}${subareaParam}`;
    case 'closingNow':             return `/api/charts/closing-now?${scope}${luxParam}${catParam}`;
    case 'shadowInventory':        return `/api/charts/shadow-inventory?${scope}${luxParam}${catParam}${subareaParam}`;
    case 'locationStats':
      return `/api/dashboard/stats?${scope}&propertyType=${pt}${luxParam}${catParam}${subareaParam}`;
    case 'neighborhoodsOverview':
      return `/api/dashboard/neighborhood-overview?marketId=${entityId}&propertyType=${pt}${luxParam}${catParam}`;
    default: return null;
  }
}

// ── PDF assembly ─────────────────────────────────────────────────────────────

interface CaptureResult { imgData: string; width: number; height: number; }

async function assembleAndDownload(pages: CaptureResult[], orgLogoUrl: string | undefined, userName: string | undefined): Promise<void> {
  const pageW = 11, pageH = 8.5, marginX = 0.5, marginTop = 1.0, marginBot = 1.0;
  const availW = pageW - 2 * marginX;
  const availH = pageH - marginTop - marginBot;

  const pdf = new jsPDF({ orientation: 'landscape', unit: 'in', format: [pageW, pageH] });

  const prismData = await fetchSvgAsDataUrl(
    prismLogo as string, Math.round(PRISM_LOGO_ASPECT * 200), 200,
  );

  let orgLogoDataUrl: string | null = null;
  let orgLogoAspect = 1;
  if (orgLogoUrl) {
    const logoImg = await loadImage(orgLogoUrl, true).catch(() => null);
    if (logoImg) {
      orgLogoAspect = logoImg.naturalWidth / logoImg.naturalHeight;
      const c = document.createElement('canvas');
      c.width = logoImg.naturalWidth; c.height = logoImg.naturalHeight;
      c.getContext('2d')!.drawImage(logoImg, 0, 0);
      orgLogoDataUrl = c.toDataURL('image/png');
    }
  }

  for (let i = 0; i < pages.length; i++) {
    if (i > 0) pdf.addPage([pageW, pageH], 'landscape');
    const { imgData, width, height } = pages[i];

    const widthIn  = width  / 96;
    const heightIn = height / 96;
    const fit  = Math.min(availW / widthIn, availH / heightIn);
    const imgW = widthIn  * fit;
    const imgH = heightIn * fit;

    pdf.addImage(imgData, 'PNG', (pageW - imgW) / 2, marginTop + (availH - imgH) / 2, imgW, imgH);

    if (orgLogoDataUrl) {
      const logoH = 0.5;
      pdf.addImage(orgLogoDataUrl, 'PNG', 0.5, 0.5, orgLogoAspect * logoH, logoH);
    }
    if (prismData) {
      const prismH = 0.5;
      pdf.addImage(prismData, 'PNG', pageW - PRISM_LOGO_ASPECT * prismH - 0.5, pageH - prismH - 0.5, PRISM_LOGO_ASPECT * prismH, prismH);
    }

    if (userName) {
      const date = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(160, 160, 160);
      pdf.text(`Lens report produced for ${userName} on ${date}`, 0.5, pageH - 0.75);
    }
  }

  pdf.save('Prism Lens.pdf');
}

// ── Off-screen renderer ──────────────────────────────────────────────────────

const TABLE_CHARTS = new Set(['buildingAppreciation', 'closingNow', 'locationStats', 'neighborhoodsOverview']);

interface BuildingAppRow {
  buildingId: number; building: string; buildingCategory: string;
  buildingAddress: string | null; units: number | null; resales: number;
  earliestSale: string; mostRecentSale: string; annualizedAppreciation: number;
}
interface ClosingNowRow {
  buildingId: number; building: string; buildingCategory: string;
  address: string | null; totalUnits: number | null; unitsSold: number | null;
  pctSoldOut: number | null; avgPrice: number | null; avgPsf: number | null;
}
interface LocationStatsRow {
  label: string;
  sales: number | null; volume: number | null;
  avgPrice: number | null; medianPrice: number | null;
  avgPsf: number | null; medianPsf: number | null;
  deltaSales: number | null; deltaVolume: number | null;
  deltaAvgPrice: number | null; deltaMedianPrice: number | null;
  deltaAvgPsf: number | null; deltaMedianPsf: number | null;
}
interface NeighborhoodOverviewItem {
  neighborhoodId: number; neighborhoodName: string;
  avgPrice: number | null; medianPrice: number | null;
  avgPsf: number | null; medianPsf: number | null;
  deltaAvgPrice: number | null; deltaMedianPrice: number | null;
  deltaAvgPsf: number | null; deltaMedianPsf: number | null;
}

const MONTH_ABBR: Record<string, string> = {
  Jan: 'January', Feb: 'February', Mar: 'March',    Apr: 'April',
  May: 'May',     Jun: 'June',     Jul: 'July',      Aug: 'August',
  Sep: 'September', Oct: 'October', Nov: 'November', Dec: 'December',
};
function expandMonthAbbr(s: string): string {
  return s.replace(/^[A-Z][a-z]{2}/, m => MONTH_ABBR[m] ?? m);
}
function fmtStatPrice(v: number | null): string {
  if (v == null) return '—';
  return `$${Math.round(v).toLocaleString('en-US')}`;
}
function fmtStatVolume(v: number | null): string {
  if (v == null) return '—';
  return v >= 1_000_000_000 ? `$${(v / 1_000_000_000).toFixed(1)}B` : `$${(v / 1_000_000).toFixed(1)}M`;
}
function fmtStatCount(v: number | null): string {
  return v == null ? '—' : v.toLocaleString('en-US');
}
function fmtStatDelta(v: number | null): string | null {
  if (v == null) return null;
  if (v > 0) return `▲ ${v.toFixed(1)}%`;
  if (v < 0) return `▼ ${Math.abs(v).toFixed(1)}%`;
  return null;
}
function statDeltaColor(v: number | null): string {
  if (v == null) return '#666';
  return v >= 0 ? '#1CA11C' : 'rgb(214,10,34)';
}

function OffscreenChart({ item, data, onCapture }: {
  item: PackageItem;
  data: any;
  onCapture: (result: CaptureResult | null) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any>(null);

  useEffect(() => {
    if (!data) { onCapture(null); return; }
    let cancelled = false;
    const delay = TABLE_CHARTS.has(item.chartName) ? 100 : 700;

    const timer = setTimeout(async () => {
      if (cancelled || !containerRef.current) { if (!cancelled) onCapture(null); return; }

      if (TABLE_CHARTS.has(item.chartName)) {
        try {
          const canvas = await html2canvas(containerRef.current, {
            scale: 2, backgroundColor: '#ffffff', useCORS: true,
          });
          if (!cancelled) onCapture({
            imgData: canvas.toDataURL('image/png'),
            width:  containerRef.current.offsetWidth,
            height: containerRef.current.offsetHeight,
          });
        } catch { if (!cancelled) onCapture(null); }
        return;
      }

      // Apply hidden series before capture
      if (item.hiddenSeries?.length) {
        const hcChart = chartRef.current?.chart;
        if (hcChart) {
          item.hiddenSeries.forEach((name: string) => {
            const s = hcChart.series.find((s: any) => s.name === name);
            s?.hide();
          });
        }
      }

      const svg = containerRef.current.querySelector('svg');
      if (!svg) { onCapture(null); return; }

      const width  = svg.clientWidth  || svg.viewBox.baseVal.width  || 1060;
      const height = svg.clientHeight || svg.viewBox.baseVal.height || 650;
      const scale  = 2;
      const blobUrl = URL.createObjectURL(
        new Blob([new XMLSerializer().serializeToString(svg)], { type: 'image/svg+xml;charset=utf-8' }),
      );
      try {
        const img = await loadImage(blobUrl);
        URL.revokeObjectURL(blobUrl);
        const c = document.createElement('canvas');
        c.width = width * scale; c.height = height * scale;
        const ctx = c.getContext('2d')!;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, c.width, c.height);
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0);
        if (!cancelled) onCapture({ imgData: c.toDataURL('image/png'), width, height });
      } catch {
        URL.revokeObjectURL(blobUrl);
        if (!cancelled) onCapture(null);
      }
    }, delay);

    return () => { cancelled = true; clearTimeout(timer); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { entityName, propertyType, buildingCategoryId, chartName, chartTitle } = item;
  const ptLabel  = PT_LABELS[propertyType ?? 'C'] ?? (propertyType ?? '');
  const catLabel = buildingCategoryId != null ? (CAT_LABELS[buildingCategoryId] ?? '') : '';

  const thSt: React.CSSProperties = {
    padding: '0.5rem 0.75rem', fontWeight: 600, fontFamily: "'Montserrat', sans-serif",
    fontSize: 13, textAlign: 'left', background: '#DCE4E5', color: '#000',
  };
  const tdSt: React.CSSProperties = {
    padding: '0.5rem 0.75rem', fontFamily: "'Montserrat', sans-serif", fontSize: 13,
  };

  let content: React.ReactElement;

  if (chartName === 'buildingAppreciation') {
    const { mode: appSortMode, range: appRange } = parseSortOrder(item.sortOrder);
    const sorted = ((data ?? []) as BuildingAppRow[]).slice().sort((a, b) =>
      appSortMode === 'openDate'
        ? new Date(b.earliestSale).getTime() - new Date(a.earliestSale).getTime()
        : b.annualizedAppreciation - a.annualizedAppreciation
    );
    const rows = appRange ? sorted.slice(appRange[0] - 1, appRange[1]) : sorted.slice(0, 10);
    const appSortSuffix = appSortMode === 'openDate' ? ' by Building Open Date' : '';
    const appSubtitle = appRange
      ? `${entityName} | ${catLabel ? `${catLabel} ` : ''}Buildings ${appRange[0]}-${appRange[1]}${appSortSuffix}`
      : `${entityName} | Top Ten ${catLabel ? `${catLabel} ` : ''}Buildings${appSortSuffix}`;
    content = (
      <div style={{ padding: '1rem', background: '#fff', width: 1060 }}>
        <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 24, fontWeight: 500, marginBottom: 8 }}>{chartTitle}</div>
        <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 13, color: '#66635F', marginBottom: 16 }}>
          {appSubtitle}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={thSt}>Building</th>
            <th style={thSt}>Address</th>
            <th style={{ ...thSt, textAlign: 'center' }}>Units</th>
            <th style={{ ...thSt, textAlign: 'center' }}>Resales</th>
            <th style={thSt}>Open Date</th>
            <th style={thSt}>Most Recent Sale</th>
            <th style={{ ...thSt, textAlign: 'right' }}>Ann. Appreciation</th>
          </tr></thead>
          <tbody>{rows.map((row, i) => (
            <tr key={row.buildingId} style={{ background: i % 2 === 0 ? '#fff' : '#F0EEEA' }}>
              <td style={tdSt}>{row.building}</td>
              <td style={tdSt}>{row.buildingAddress ?? '—'}</td>
              <td style={{ ...tdSt, textAlign: 'center' }}>{row.units ?? '—'}</td>
              <td style={{ ...tdSt, textAlign: 'center' }}>{row.resales}</td>
              <td style={tdSt}>{row.earliestSale?.slice(0, 10) ?? '—'}</td>
              <td style={tdSt}>{row.mostRecentSale?.slice(0, 10) ?? '—'}</td>
              <td style={{ ...tdSt, textAlign: 'right', fontWeight: 600, color: row.annualizedAppreciation < 0 ? 'rgb(214,10,34)' : '#1CA11C' }}>
                {(row.annualizedAppreciation * 100).toFixed(1)}%
              </td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    );
  } else if (chartName === 'closingNow') {
    const rows = ((data ?? []) as ClosingNowRow[]).slice(0, 10);
    content = (
      <div style={{ padding: '1rem', background: '#fff', width: 1060 }}>
        <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 24, fontWeight: 500, marginBottom: 8 }}>{chartTitle}</div>
        <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 13, color: '#66635F', marginBottom: 16 }}>
          {entityName}{catLabel ? ` | ${catLabel}` : ''}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={thSt}>Building</th>
            <th style={thSt}>Address</th>
            <th style={{ ...thSt, textAlign: 'center' }}>Total Units</th>
            <th style={{ ...thSt, textAlign: 'center' }}>Units Sold</th>
            <th style={{ ...thSt, textAlign: 'center' }}>% Sold Out</th>
            <th style={{ ...thSt, textAlign: 'right' }}>Avg Price</th>
            <th style={{ ...thSt, textAlign: 'right' }}>Avg PSF</th>
          </tr></thead>
          <tbody>{rows.map((row, i) => (
            <tr key={row.buildingId} style={{ background: i % 2 === 0 ? '#fff' : '#F0EEEA' }}>
              <td style={tdSt}>{row.building}</td>
              <td style={tdSt}>{row.address ?? '—'}</td>
              <td style={{ ...tdSt, textAlign: 'center' }}>{row.totalUnits ?? '—'}</td>
              <td style={{ ...tdSt, textAlign: 'center' }}>{row.unitsSold ?? '—'}</td>
              <td style={{ ...tdSt, textAlign: 'center' }}>{row.pctSoldOut != null ? `${(row.pctSoldOut * 100).toFixed(1)}%` : '—'}</td>
              <td style={{ ...tdSt, textAlign: 'right' }}>{row.avgPrice != null ? `$${row.avgPrice.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—'}</td>
              <td style={{ ...tdSt, textAlign: 'right' }}>{row.avgPsf  != null ? `$${row.avgPsf.toLocaleString('en-US',  { maximumFractionDigits: 0 })}` : '—'}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    );
  } else if (chartName === 'locationStats') {
    const period = ((data ?? []) as LocationStatsRow[])[0] ?? null;
    const periodEnd = period ? expandMonthAbbr(period.label.split('–')[1]?.trim() ?? '') : '';
    const cards: Array<{ label: string; value: string; delta: number | null }> = period ? [
      { label: 'Average Price',  value: fmtStatPrice(period.avgPrice),    delta: period.deltaAvgPrice },
      { label: 'Median Price',   value: fmtStatPrice(period.medianPrice), delta: period.deltaMedianPrice },
      { label: 'Sales',          value: fmtStatCount(period.sales),       delta: period.deltaSales },
      { label: 'Volume',         value: fmtStatVolume(period.volume),     delta: period.deltaVolume },
      ...(period.avgPsf    != null ? [{ label: 'Avg PSF',    value: fmtStatPrice(period.avgPsf),    delta: period.deltaAvgPsf    }] : []),
      ...(period.medianPsf != null ? [{ label: 'Median PSF', value: fmtStatPrice(period.medianPsf), delta: period.deltaMedianPsf }] : []),
    ] : [];
    content = (
      <div style={{ padding: '1rem', background: '#fff', width: 1060 }}>
        <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 24, fontWeight: 500, marginBottom: 4 }}>Metrics</div>
        <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 13, color: '#66635F', marginBottom: 16 }}>
          {[
            entityName,
            [catLabel, ptLabel].filter(Boolean).join(' '),
            periodEnd ? `12 Month Period Ending ${periodEnd}` : '',
          ].filter(Boolean).join(' | ')}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
          {cards.map(card => (
            <div key={card.label} style={{ background: '#fff', border: '1px solid #ddd', borderTop: '3px solid #958945', padding: '1rem 1.25rem', fontFamily: "'Montserrat', sans-serif" }}>
              <div style={{ fontSize: 13, color: '#555', marginBottom: '0.35rem' }}>{card.label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#000', marginBottom: '0.35rem' }}>{card.value}</div>
              {card.delta != null && (
                Number(card.delta.toFixed(1)) === 0
                  ? <div style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>No Change vs Previous Period</div>
                  : <div style={{ fontSize: 12, fontWeight: 600, color: statDeltaColor(card.delta) }}>{fmtStatDelta(card.delta)} vs Previous Period</div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  } else if (chartName === 'neighborhoodsOverview') {
    const rows = (data ?? []) as NeighborhoodOverviewItem[];
    const fmtCell = (value: number | null, delta: number | null): React.ReactElement => {
      const formatted = fmtStatPrice(value);
      if (delta == null || Number(delta.toFixed(1)) === 0)
        return <span style={{ color: '#000' }}>{formatted}</span>;
      const color = delta > 0 ? '#1CA11C' : 'rgb(214,10,34)';
      return <span><span style={{ color }}>{delta > 0 ? '▲' : '▼'} </span><span style={{ color: '#000' }}>{formatted}</span></span>;
    };
    content = (
      <div style={{ padding: '1rem', background: '#fff', width: 1060 }}>
        <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 24, fontWeight: 500, marginBottom: 4 }}>Neighborhoods</div>
        <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: 13, color: '#66635F', marginBottom: 16 }}>
          {[entityName, [catLabel, ptLabel].filter(Boolean).join(' ')].filter(Boolean).join(' | ')}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thSt}>Neighborhood</th>
              <th style={{ ...thSt, textAlign: 'right' }}>Avg Price</th>
              <th style={{ ...thSt, textAlign: 'right' }}>Median Price</th>
              <th style={{ ...thSt, textAlign: 'right' }}>Avg PSF</th>
              <th style={{ ...thSt, textAlign: 'right' }}>Median PSF</th>
            </tr>
          </thead>
          <tbody>
            {[...rows].sort((a, b) => a.neighborhoodName.localeCompare(b.neighborhoodName)).map((row, i) => (
              <tr key={row.neighborhoodId} style={{ background: i % 2 === 0 ? '#fff' : '#F0EEEA' }}>
                <td style={tdSt}>{row.neighborhoodName}</td>
                <td style={{ ...tdSt, textAlign: 'right' }}>{fmtCell(row.avgPrice,    row.deltaAvgPrice)}</td>
                <td style={{ ...tdSt, textAlign: 'right' }}>{fmtCell(row.medianPrice, row.deltaMedianPrice)}</td>
                <td style={{ ...tdSt, textAlign: 'right' }}>{fmtCell(row.avgPsf,      row.deltaAvgPsf)}</td>
                <td style={{ ...tdSt, textAlign: 'right' }}>{fmtCell(row.medianPsf,   row.deltaMedianPsf)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  } else {
    const cd = data as any;
    let chart: React.ReactElement = <div />;
    switch (chartName) {
      case 'monthlyClosings':
        chart = <MonthlyClosingsChart ref={chartRef} data={cd} title={chartTitle} neighborhoodName={entityName} propertyTypeLabel={ptLabel} />; break;
      case 'millionDollarMarket':
        chart = <MillionDollarMarketChart ref={chartRef} data={cd} title={chartTitle} neighborhoodName={entityName} propertyTypeLabel={ptLabel} />; break;
      case 'salesPrices':
        chart = <SalesPricesChart ref={chartRef} data={cd} title={chartTitle} neighborhoodName={entityName} propertyTypeLabel={ptLabel} buildingCategoryLabel={catLabel} />; break;
      case 'salesPSF':
        chart = <SalesPSFChart ref={chartRef} data={cd} title={chartTitle} neighborhoodName={entityName} propertyTypeLabel={ptLabel} buildingCategoryLabel={catLabel} />; break;
      case 'salesPriceDistribution':
        chart = <SalesPriceDistributionChart ref={chartRef} data={cd} title={chartTitle} neighborhoodName={entityName} propertyTypeLabel={ptLabel} />; break;
      case 'salesDistribution':
        chart = <SalesDistributionChart ref={chartRef} data={cd} title={chartTitle} neighborhoodName={entityName} />; break;
      case 'luxVsTraditional':
        chart = <LuxVsTraditionalChart ref={chartRef} data={cd} title={chartTitle} neighborhoodName={entityName} />; break;
      case 'priceIndex':
        chart = <PriceIndexChart ref={chartRef} data={{ categories: cd.categories, series: cd.series }} title={chartTitle} neighborhoodName={entityName} propertyTypeLabel={ptLabel} timePeriods={cd.timePeriods ?? []} />; break;
      case 'shadowInventory':
        chart = <ShadowInventoryChart ref={chartRef} data={cd} title={chartTitle} neighborhoodName={entityName} buildingCategoryLabel={catLabel} />; break;
    }
    content = chart;
  }

  const isTable = TABLE_CHARTS.has(chartName);
  return (
    <div style={{ position: 'fixed', left: -9999, top: 0, width: 1060, overflow: 'hidden' }}>
      <div ref={containerRef} style={{ width: 1060, height: isTable ? undefined : 650 }}>
        {content}
      </div>
    </div>
  );
}

// ── Panel ────────────────────────────────────────────────────────────────────

type ExportPhase = 'idle' | 'fetching' | 'capturing' | 'assembling';

export function AnalyticsPackagePanel() {
  const {
    items, panelOpen, setPanelOpen, removeItem, reorderItems, clearItems,
    activeLensId, activeLensName, setActiveLens, setMyLensesPanelOpen,
  } = useAnalyticsPackage();
  const [dragIdx,     setDragIdx]     = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const { authFetch } = useApi();

  const [isAdmin,        setIsAdmin]        = useState(false);
  const isAdminFetched                       = useRef(false);
  const [namingOpen,     setNamingOpen]     = useState(false);
  const [namingMode,     setNamingMode]     = useState<'new' | 'saveas'>('new');
  const [lensName,       setLensName]       = useState('');
  const [saveAsSystem,   setSaveAsSystem]   = useState(false);
  const [entityScope,    setEntityScope]    = useState<'market' | 'neighborhood'>('market');
  const [isSavingLens,   setIsSavingLens]   = useState(false);

  useEffect(() => {
    if (!panelOpen || isAdminFetched.current) return;
    isAdminFetched.current = true;
    authFetch('/api/me').then(r => r.json()).then((d: any) => {
      if (d?.isAdmin) setIsAdmin(true);
    }).catch(() => {});
  }, [panelOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNaming = (mode: 'new' | 'saveas') => {
    setNamingMode(mode);
    setLensName(mode === 'saveas' ? (activeLensName ?? '') : '');
    setSaveAsSystem(false);
    setNamingOpen(true);
  };

  const handleOverwrite = async () => {
    if (!activeLensId || isSavingLens) return;
    setIsSavingLens(true);
    try {
      await authFetch(`/api/lenses/${activeLensId}`, {
        method: 'PUT',
        body: JSON.stringify({ items: items.map(i => ({
          entityType: i.entityType, entityId: i.entityId, entityName: i.entityName,
          chartName: i.chartName, chartTitle: i.chartTitle, propertyType: i.propertyType,
          buildingCategoryId: i.buildingCategoryId, yearStart: i.yearStart, yearEnd: i.yearEnd,
          subareaId: i.subareaId, subareaName: i.subareaName, sortKey: i.sortOrder,
          hiddenSeries: i.hiddenSeries?.length ? i.hiddenSeries : null,
        })) }),
      });
    } catch { /* non-fatal */ }
    setIsSavingLens(false);
  };

  const handleSaveConfirm = async () => {
    const name = lensName.trim();
    if (!name || isSavingLens) return;
    setIsSavingLens(true);
    try {
      const res = await authFetch('/api/lenses', {
        method: 'POST',
        body: JSON.stringify({
          name,
          isSystem: saveAsSystem,
          entityScope: saveAsSystem ? entityScope : null,
          items: items.map(i => ({
            entityType: i.entityType, entityId: i.entityId, entityName: i.entityName,
            chartName: i.chartName, chartTitle: i.chartTitle, propertyType: i.propertyType,
            buildingCategoryId: i.buildingCategoryId, yearStart: i.yearStart, yearEnd: i.yearEnd,
            subareaId: i.subareaId, subareaName: i.subareaName, sortKey: i.sortOrder,
          })),
        }),
      });
      if (res.ok) {
        const saved: SavedLensDto = await res.json();
        setActiveLens(saved.id, saved.name);
      }
    } catch { /* non-fatal */ }
    setIsSavingLens(false);
    setNamingOpen(false);
  };

  const [exportPhase, setExportPhase] = useState<ExportPhase>('idle');
  const [allData,       setAllData]       = useState<any[]>([]);
  const [captureIdx,    setCaptureIdx]    = useState(0);
  const [capturedPages, setCapturedPages] = useState<(CaptureResult | null)[]>([]);
  const [fetchIdx,      setFetchIdx]      = useState(0);
  const orgLogoUrlRef = useRef<string | undefined>(undefined);
  const userNameRef   = useRef<string | undefined>(undefined);

  // Phase: fetching — sequential API calls to avoid DB overload
  useEffect(() => {
    if (exportPhase !== 'fetching') return;
    let cancelled = false;

    (async () => {
      // Get org logo URL
      try {
        const meRes  = await authFetch('/api/me');
        const meData = await meRes.json();
        if (meData?.organization?.logoUrl) {
          orgLogoUrlRef.current = `${process.env.REACT_APP_API_URL ?? ''}/api/organizations/${meData.organization.id}/logo-bytes`;
        }
        userNameRef.current = meData?.displayName?.trim() || meData?.email || undefined;
      } catch { /* non-fatal */ }

      if (cancelled) return;

      const results: any[] = [];
      for (let i = 0; i < items.length; i++) {
        if (cancelled) return;
        setFetchIdx(i);
        const url = itemApiUrl(items[i]);
        if (!url) { results.push(null); continue; }
        try {
          const res = await authFetch(url);
          results.push(await res.json());
        } catch { results.push(null); }
      }

      if (cancelled) return;
      setAllData(results);
      setCaptureIdx(0);
      setCapturedPages([]);
      Highcharts.setOptions({ plotOptions: { series: { animation: false } } });
      setExportPhase('capturing');
    })();

    return () => { cancelled = true; };
  }, [exportPhase]); // eslint-disable-line react-hooks/exhaustive-deps

  // Phase: assembling — triggered when all captures are in
  useEffect(() => {
    if (exportPhase !== 'capturing') return;
    if (capturedPages.length < items.length || items.length === 0) return;

    Highcharts.setOptions({ plotOptions: { series: { animation: true } } });
    setExportPhase('assembling');
    const valid = capturedPages.filter((p): p is CaptureResult => p !== null);
    assembleAndDownload(valid, orgLogoUrlRef.current, userNameRef.current).then(() => {
      setExportPhase('idle');
      setCapturedPages([]);
      setCaptureIdx(0);
      setAllData([]);
      orgLogoUrlRef.current = undefined;
      userNameRef.current   = undefined;
    });
  }, [capturedPages, items.length, exportPhase]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCapture = (result: CaptureResult | null) => {
    setCapturedPages(prev => [...prev, result]);
    setCaptureIdx(prev => prev + 1);
  };

  const startExport = () => {
    if (items.length === 0 || exportPhase !== 'idle') return;
    orgLogoUrlRef.current = undefined;
    userNameRef.current   = undefined;
    setFetchIdx(0);
    setExportPhase('fetching');
  };

  const exporting = exportPhase !== 'idle';

  useEffect(() => {
    if (!panelOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape' && !exporting) setPanelOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [panelOpen, exporting, setPanelOpen]);

  const exportLabel =
    exportPhase === 'fetching'   ? `Fetching ${Math.min(fetchIdx + 1, items.length)} of ${items.length}…` :
    exportPhase === 'capturing'  ? `Rendering ${Math.min(captureIdx + 1, items.length)} of ${items.length}…` :
    exportPhase === 'assembling' ? 'Assembling PDF…' :
    'Export PDF';

  return (
    <>
      {panelOpen && (
        <div
          onClick={() => { if (!exporting) setPanelOpen(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 1100 }}
        />
      )}

      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 380,
        background: '#fff', boxShadow: panelOpen ? '-4px 0 20px rgba(0,0,0,0.15)' : 'none',
        zIndex: 1200, transform: panelOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.25s ease',
        fontFamily: "'Montserrat', sans-serif",
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '1.5rem 1.5rem 1rem', borderBottom: '1px solid #eee', flexShrink: 0,
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: 18, fontWeight: 600 }}>Prism Lens</span>
              {items.length > 0 && (
                <span style={{
                  background: '#ba9394', color: '#fff', borderRadius: 10,
                  fontSize: 12, fontWeight: 700, padding: '2px 8px',
                }}>
                  {items.length}
                </span>
              )}
            </div>
            {activeLensName && (
              <div style={{ fontSize: 12, color: '#ba9394', fontWeight: 500 }}>
                {activeLensName}
              </div>
            )}
          </div>
          <button
            onClick={() => { if (!exporting) setPanelOpen(false); }}
            style={{ background: 'none', border: 'none', fontSize: 20, cursor: exporting ? 'default' : 'pointer', color: '#888' }}
          >
            ✕
          </button>
        </div>

        {/* Items list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.5rem' }}>
          {items.length === 0 ? (
            <div style={{ fontSize: 14, marginTop: '1rem', lineHeight: 1.6 }}>
              <div style={{ color: '#555', marginBottom: '0.6rem' }}>
                No insights added yet. Use the <strong>+ Add to Lens</strong> button anywhere you see it to build a Lens.
              </div>
              <div style={{ color: '#888' }}>
                Or, load a saved Lens or use a Template from the{' '}
                <button
                  onClick={() => setMyLensesPanelOpen(true)}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#ba9394', fontSize: 14, fontFamily: "'Montserrat', sans-serif", fontWeight: 600, textDecoration: 'underline' }}
                >
                  Lenses
                </button>{' '}
                menu.
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {items.map((item, idx) => {
                const lbl = sortLabel(item.sortOrder);
                const summary = [
                  filterSummary(item.propertyType, item.buildingCategoryId, item.yearStart, item.yearEnd, item.subareaName),
                  lbl,
                ].filter(Boolean).join(' · ');
                const isDragging  = dragIdx     === idx;
                const isDragOver  = dragOverIdx === idx;
                return (
                  <div
                    key={item.key}
                    draggable={!exporting}
                    onDragStart={() => setDragIdx(idx)}
                    onDragOver={e => { e.preventDefault(); setDragOverIdx(idx); }}
                    onDrop={() => { if (dragIdx !== null) reorderItems(dragIdx, idx); setDragIdx(null); setDragOverIdx(null); }}
                    onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                    style={{
                      background: '#f8f8f6',
                      border: isDragOver ? '2px solid #ba9394' : '1px solid #e8e8e4',
                      borderRadius: 8,
                      padding: isDragOver ? '0.688rem 0.938rem' : '0.75rem 1rem',
                      display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                      opacity: isDragging ? 0.4 : 1,
                      cursor: exporting ? 'default' : 'grab',
                      userSelect: 'none',
                    }}
                  >
                    <span style={{ color: '#ccc', fontSize: 14, flexShrink: 0, marginTop: '0.1rem', lineHeight: 1 }}>⠿</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#000', marginBottom: '0.2rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.chartTitle}
                      </div>
                      <div style={{ fontSize: 12, color: '#66635F', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.entityName}
                      </div>
                      {summary && (
                        <div style={{ fontSize: 11, color: '#999', marginTop: '0.15rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {summary}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => { if (!exporting) removeItem(item.key); }}
                      title="Remove"
                      style={{ background: 'none', border: 'none', cursor: exporting ? 'default' : 'pointer', color: '#bbb', fontSize: 16, padding: '0 0.1rem', flexShrink: 0, lineHeight: 1 }}
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #eee', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <button
              onClick={startExport}
              disabled={exporting}
              style={{
                width: '100%', padding: '0.65rem', fontSize: 14, fontWeight: 600,
                background: '#ba9394', color: '#fff', border: 'none', borderRadius: 6,
                cursor: exporting ? 'not-allowed' : 'pointer',
                opacity: exporting ? 0.75 : 1,
                fontFamily: "'Montserrat', sans-serif",
              }}
            >
              {exportLabel}
            </button>
            {!exporting && (
              <>
                {activeLensId ? (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={handleOverwrite}
                      disabled={isSavingLens}
                      style={{
                        flex: 1, padding: '0.5rem', fontSize: 13, fontWeight: 500,
                        background: '#2c5c4e', color: '#fff', border: 'none', borderRadius: 6,
                        cursor: isSavingLens ? 'not-allowed' : 'pointer',
                        opacity: isSavingLens ? 0.65 : 1,
                        fontFamily: "'Montserrat', sans-serif",
                      }}
                    >
                      Save
                    </button>
                    <button
                      onClick={() => openNaming('saveas')}
                      disabled={isSavingLens}
                      style={{
                        flex: 1, padding: '0.5rem', fontSize: 13, fontWeight: 500,
                        background: 'none', color: '#2c5c4e', border: '1px solid #2c5c4e', borderRadius: 6,
                        cursor: 'pointer', fontFamily: "'Montserrat', sans-serif",
                      }}
                    >
                      Save As…
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => openNaming('new')}
                    style={{
                      width: '100%', padding: '0.5rem', fontSize: 13, fontWeight: 500,
                      background: '#2c5c4e', color: '#fff', border: 'none', borderRadius: 6,
                      cursor: 'pointer', fontFamily: "'Montserrat', sans-serif",
                    }}
                  >
                    Save Lens…
                  </button>
                )}
                <button
                  onClick={clearItems}
                  style={{
                    width: '100%', padding: '0.5rem', fontSize: 13, fontWeight: 500,
                    background: 'none', color: '#999', border: '1px solid #e0e0e0', borderRadius: 6,
                    cursor: 'pointer', fontFamily: "'Montserrat', sans-serif",
                  }}
                >
                  Clear Lens
                </button>
              </>
            )}
          </div>
        )}

        {/* Naming sub-panel — slides in over the main panel */}
        <div style={{
          position: 'absolute', inset: 0,
          background: '#fff',
          transform: namingOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.2s ease',
          display: 'flex', flexDirection: 'column',
          fontFamily: "'Montserrat', sans-serif",
          zIndex: 10,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.75rem',
            padding: '1.5rem 1.5rem 1rem', borderBottom: '1px solid #eee', flexShrink: 0,
          }}>
            <button
              onClick={() => setNamingOpen(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#555', lineHeight: 1, padding: 0 }}
            >
              ‹
            </button>
            <span style={{ fontSize: 16, fontWeight: 600 }}>
              {namingMode === 'saveas' ? 'Save As New Lens' : 'Save Lens'}
            </span>
          </div>

          <div style={{ flex: 1, padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: '0.4rem' }}>
                Lens Name
              </label>
              <input
                type="text"
                value={lensName}
                onChange={e => setLensName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSaveConfirm(); }}
                placeholder="e.g. Market Overview Q3"
                style={{
                  width: '100%', padding: '0.6rem 0.75rem', fontSize: 14,
                  border: '1px solid #ccc', borderRadius: 6, outline: 'none',
                  fontFamily: "'Montserrat', sans-serif",
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {isAdmin && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={saveAsSystem}
                  onChange={e => setSaveAsSystem(e.target.checked)}
                />
                Save as System Template
              </label>
            )}

            {isAdmin && saveAsSystem && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: '0.4rem' }}>Template Scope</div>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  {(['market', 'neighborhood'] as const).map(scope => (
                    <label key={scope} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: 13, textTransform: 'capitalize' }}>
                      <input type="radio" name="entityScope" value={scope} checked={entityScope === scope} onChange={() => setEntityScope(scope)} />
                      {scope}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #eee', flexShrink: 0 }}>
            <button
              onClick={handleSaveConfirm}
              disabled={!lensName.trim() || isSavingLens}
              style={{
                width: '100%', padding: '0.65rem', fontSize: 14, fontWeight: 600,
                background: '#2c5c4e', color: '#fff', border: 'none', borderRadius: 6,
                cursor: !lensName.trim() || isSavingLens ? 'not-allowed' : 'pointer',
                opacity: !lensName.trim() || isSavingLens ? 0.6 : 1,
                fontFamily: "'Montserrat', sans-serif",
              }}
            >
              {isSavingLens ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {/* Off-screen chart renderer — mounts one chart at a time during capture phase */}
      {exportPhase === 'capturing' && captureIdx < items.length && allData.length === items.length && (
        <OffscreenChart
          key={captureIdx}
          item={items[captureIdx]}
          data={allData[captureIdx]}
          onCapture={handleCapture}
        />
      )}
    </>
  );
}
